import { ColDef, ValueFormatterParams } from 'ag-grid-community'
import columnRenderers, { PLACEHOLDER } from 'components/column-renderers'
import { CSVField } from 'hooks/useExportCSV'
import {
  EPartnerCodeStatus,
  EPartnerCodeStatusLabels,
  EPartnerCodeType,
  EPartnerCodeTypeLabels,
  PartnerCode,
} from 'types'
import { formatDate } from 'utils'
import { AccessManagementColumnCode } from '../AccessManagementColumnCode/AccessManagementColumnCode'

const labelFormatter =
  <T extends string>(labels: Record<T, string>) =>
  ({ value }: ValueFormatterParams) => {
    if (!value) return PLACEHOLDER
    return labels[value as T] ?? value
  }

const codeTypeLabel = (codeType?: EPartnerCodeType) =>
  codeType ? EPartnerCodeTypeLabels[codeType] ?? codeType : PLACEHOLDER

const statusLabel = (status?: EPartnerCodeStatus) =>
  status ? EPartnerCodeStatusLabels[status] ?? status : PLACEHOLDER

const countOrPlaceholder = (count?: number | null) => (count == null ? PLACEHOLDER : String(count))

/**
 * License counters come from the backend twice: as the database columns `numberOfUsers` /
 * `timesUsed` and as the spec-named aliases `totalLicenses` / `licensesUsed`. Columns bind to the
 * database names so sorting keeps working — the backend orders by the raw field. `licensesRemaining`
 * exists only in the DTO, so that column cannot be sorted.
 *
 * Unfunded codes have no user limit: their cap and remainder are null and render as a dash, while
 * usage is still counted.
 */
export const partnerCodeColumns: ColDef[] = [
  {
    headerName: 'Code Type',
    field: 'codeType',
    minWidth: 180,
    valueFormatter: labelFormatter(EPartnerCodeTypeLabels),
  },
  {
    headerName: 'Code Value',
    cellRenderer: AccessManagementColumnCode,
    field: 'code',
    colId: 'code',
    minWidth: 260,
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Organization / Partner',
    field: 'organizationName',
    minWidth: 240,
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Total Licenses',
    field: 'numberOfUsers',
    minWidth: 180,
  },
  {
    headerName: 'Licenses Used',
    field: 'timesUsed',
    minWidth: 180,
  },
  {
    headerName: 'Licenses Remaining',
    field: 'licensesRemaining',
    minWidth: 200,
    sortable: false,
  },
  {
    headerName: 'Status',
    field: 'status',
    minWidth: 150,
    valueFormatter: labelFormatter(EPartnerCodeStatusLabels),
  },
  {
    headerName: 'Creation Date',
    cellRenderer: columnRenderers.date('createdAt'),
    colId: 'createdAt',
    minWidth: 200,
  },
  {
    headerName: 'Name / Last Name',
    colId: 'contactFirstName',
    minWidth: 220,
    valueGetter: (value) => {
      if (!value.data) return PLACEHOLDER

      const name = [value.data.contactFirstName, value.data.contactLastName]
        .filter(Boolean)
        .join(' ')
      return name || PLACEHOLDER
    },
  },
  {
    headerName: 'Email',
    field: 'contactEmail',
    minWidth: 240,
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Phone',
    field: 'contactPhone',
    minWidth: 200,
  },
  {
    headerName: 'Note',
    field: 'note',
    minWidth: 240,
    sortable: false,
  },
]

export const csvPartnerCodeColumns: CSVField[] = [
  {
    label: 'Code Type',
    value: ({ codeType }: PartnerCode) => codeTypeLabel(codeType),
  },
  { label: 'Code Value', value: 'code' },
  { label: 'Organization / Partner', value: 'organizationName' },
  {
    label: 'Total Licenses',
    value: ({ numberOfUsers }: PartnerCode) => countOrPlaceholder(numberOfUsers),
  },
  { label: 'Licenses Used', value: 'timesUsed' },
  {
    label: 'Licenses Remaining',
    value: ({ licensesRemaining }: PartnerCode) => countOrPlaceholder(licensesRemaining),
  },
  {
    label: 'Status',
    value: ({ status }: PartnerCode) => statusLabel(status),
  },
  {
    label: 'Creation Date',
    value: ({ createdAt }: PartnerCode) => (createdAt ? formatDate(createdAt) : PLACEHOLDER),
  },
  {
    label: 'Name / Last Name',
    value: ({ contactFirstName, contactLastName }: PartnerCode) =>
      [contactFirstName, contactLastName].filter(Boolean).join(' ') || PLACEHOLDER,
  },
  {
    label: 'Email',
    value: ({ contactEmail }: PartnerCode) => contactEmail || PLACEHOLDER,
  },
  {
    label: 'Phone',
    value: ({ contactPhone }: PartnerCode) => contactPhone || PLACEHOLDER,
  },
  {
    label: 'Note',
    value: ({ note }: PartnerCode) => note || PLACEHOLDER,
  },
]
